import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/common/Icon';
import { isBlank } from '@/utils/formValidation';
import { buildNewOptionRow, type EditableOptionRow } from './preVisitFormEditorState';

interface PreVisitQuestionOptionsEditorProps {
  options: EditableOptionRow[];
  showValidation: boolean;
  onChange: (options: EditableOptionRow[]) => void;
}

export function PreVisitQuestionOptionsEditor({
  options,
  showValidation,
  onChange,
}: PreVisitQuestionOptionsEditorProps) {
  const { t } = useTranslation('consultations');

  function updateOption(key: string, patch: Partial<EditableOptionRow>) {
    onChange(
      options.map(function apply(option) {
        return option.key === key ? { ...option, ...patch } : option;
      })
    );
  }

  function removeOption(key: string) {
    onChange(
      options.filter(function keep(option) {
        return option.key !== key;
      })
    );
  }

  function addOption() {
    onChange([...options, buildNewOptionRow()]);
  }

  function moveOption(index: number, direction: -1 | 1) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= options.length) {
      return;
    }
    const reordered = [...options];
    const movedOption = reordered[index];
    const displacedOption = reordered[targetIndex];
    if (movedOption === undefined || displacedOption === undefined) {
      return;
    }
    reordered[index] = displacedOption;
    reordered[targetIndex] = movedOption;
    onChange(reordered);
  }

  const filledOptionCount = options.filter(function isFilled(option) {
    return isBlank(option.labelEn) === false && isBlank(option.labelPl) === false;
  }).length;

  return (
    <div className="space-y-2">
      <p className="text-xs font-bold uppercase tracking-wider text-zoomuted">
        {t('preVisitEditor.optionsTitle')}
      </p>
      <div className="space-y-2">
        {options.map(function renderOption(option, index) {
          return (
            <div key={option.key} className="flex items-center gap-2">
              <Input
                value={option.labelEn}
                placeholder={t('preVisitEditor.optionLabelEn')}
                hasError={showValidation && isBlank(option.labelEn)}
                onChange={function update(changeEvent) {
                  updateOption(option.key, { labelEn: changeEvent.target.value });
                }}
              />
              <Input
                value={option.labelPl}
                placeholder={t('preVisitEditor.optionLabelPl')}
                hasError={showValidation && isBlank(option.labelPl)}
                onChange={function update(changeEvent) {
                  updateOption(option.key, { labelPl: changeEvent.target.value });
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('preVisitEditor.moveUp')}
                disabled={index === 0}
                onClick={function moveUp() {
                  moveOption(index, -1);
                }}
                className="w-8 h-8 shrink-0"
              >
                <Icon name="ChevronDown" className="rotate-180" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('preVisitEditor.moveDown')}
                disabled={index === options.length - 1}
                onClick={function moveDown() {
                  moveOption(index, 1);
                }}
                className="w-8 h-8 shrink-0"
              >
                <Icon name="ChevronDown" />
              </Button>
              <Button
                type="button"
                variant="destructiveGhost"
                size="icon"
                aria-label={t('preVisitEditor.removeOption')}
                onClick={function remove() {
                  removeOption(option.key);
                }}
                className="w-8 h-8 shrink-0"
              >
                <Icon name="Trash" />
              </Button>
            </div>
          );
        })}
      </div>
      {showValidation && filledOptionCount < 2 ? (
        <p className="text-xs font-medium text-zooerrortext">
          {t('preVisitEditor.optionsMinimum')}
        </p>
      ) : null}
      <Button type="button" variant="outline" size="small" onClick={addOption}>
        <Icon name="Plus" /> {t('preVisitEditor.addOption')}
      </Button>
    </div>
  );
}
